import { registerLocaleData } from '@angular/common';
import { Injectable } from '@angular/core';

import {TranslateService} from "@ngx-translate/core";
import localePt from '@angular/common/locales/pt';
import { pl, enGB, pt } from 'date-fns/locale';
import { PrimeNG } from 'primeng/config';

import { LanguageCode, LanguageViewModel } from '@shared/view-models/language.viewmodel';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  private readonly _supportedLanguages: LanguageViewModel[] = [
    { code: LanguageCode.PL, locale: 'pl-PL', currency: 'PLN' } as LanguageViewModel,
    { code: LanguageCode.EN, locale: 'en-GB', currency: 'PLN'} as LanguageViewModel,
    { code: LanguageCode.PT, locale: 'pt', currency: 'PLN'} as LanguageViewModel,
  ];

  private _current = LanguageCode.PL;

  get current(): LanguageCode {
    return this._current;
  }
  set current(code: LanguageCode) {
    this._current = code;
    localStorage.setItem('language', code);
    this.initLanguage();
  }

  get currentLanguageViewModel() : LanguageViewModel {
    return this.supportedLanguages.find(l => l.code === this.current)!;
  }

  get supportedLanguages(): LanguageViewModel[] {
    return this._supportedLanguages;
  }

  public get localeVariableDateFns(): Locale {
    if (this.current === LanguageCode.PL) {
      return pl;
    }
    if (this.current === LanguageCode.EN) {
      return enGB;
    }
    if (this.current === LanguageCode.PT) {
      return pt;
    }

    return enGB;
  }

  constructor(
    private translateService: TranslateService,
    private primeNgConfig: PrimeNG,
  ) {
    registerLocaleData(localePt, LanguageCode.PT);

    this._current = this.initDefaultLanguage();
    this.initLanguage();
  }

  public get isPolishLanguage(): boolean {
    return this.current === LanguageCode.PL;
  }

  public get isEnglishLanguage(): boolean {
    return this.current === LanguageCode.EN;
  }

  public get isPortugueseLanguage(): boolean {
    return this.current === LanguageCode.PT;
  }

  private initDefaultLanguage(): LanguageCode {
    const storedLang = localStorage.getItem('language') as LanguageCode;

    if (storedLang && this._supportedLanguages.some(l => l.code === storedLang)) {
      return storedLang;
    }

    const browserLang = navigator.language.split('-')[0] as LanguageCode;
    if (this._supportedLanguages.some(l => l.code === browserLang)) {
      return browserLang;
    }

    return LanguageCode.EN;
  }

  private initLanguage() {
    this.translateService.use(this._current).subscribe(() => {
      this.setLanguageDisplayedName();
      this.initPrimeNgTranslate();
    });
  }

  private setLanguageDisplayedName() {
    for (let lang of this._supportedLanguages)
      this.translateService.get('language.' + lang.code).subscribe(t => lang.displayedName = t);
  }

  private initPrimeNgTranslate(): void {
    this.translateService.get('primeng').subscribe(res => this.primeNgConfig.setTranslation(res));
  }

}
